import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import styles from '../assets/forgotPassword.module.css';

const ResetPassword = () => {
  const [form, setForm] = useState({ email: '', password: '', password_confirmation: '', token: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useParams(); // For Laravel-style routes like /password/reset/:token

  React.useEffect(() => {
    // Extract token and email from multiple sources for compatibility
    
    // 1. Laravel-style URL parameter (e.g., /password/reset/:token)
    let token = urlParams.token;
    
    // 2. Query parameters (e.g., /reset-password?token=abc&email=user@example.com)
    const queryParams = new URLSearchParams(location.search);
    if (!token) {
      token = queryParams.get('token');
    }
    const email = queryParams.get('email');
    
    // Update form state
    if (token) setForm(f => ({ ...f, token }));
    if (email) setForm(f => ({ ...f, email }));
  }, [location.search, urlParams.token]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    // Client-side validation
    if (form.password !== form.password_confirmation) {
      setError('Passwords do not match.');
      setIsLoading(false);
      return;
    }

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email,
          token: form.token,
          password: form.password
        })
      });

      const data = await response.json();

      if (response.ok && data.password_reset) {
        setSuccess(data.message || 'Password reset successful! You can now log in.');
        setTimeout(() => {
          navigate('/login', {
            state: {
              message: 'Password reset successful! You can now log in with your new password.'
            }
          });
        }, 2000);
      } else {
        setError(data.message || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.unauthPage}>
      {/* Header */}
      <header id="page-header" className={styles.pageHeader}>
        <div className={styles.textCenter}>
          <a href="/">
            <img className={styles.logo} src="/images/logo-white.png" width="200" alt="MYGLOBE" />
          </a>
        </div>
      </header>

      {/* Content */}
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.colMdPush4}>
            <div className={styles.colMd4}>
              <div className={styles.loginForm}>
                <h3>
                  <i className="fa fa-user"></i> Reset password
                </h3>

                <form onSubmit={handleSubmit}>
                  <input type="hidden" name="token" value={form.token} />
                  
                  <div className={styles.formGroup}>
                    <input 
                      type="email" 
                      name="email" 
                      className={`${styles.formControl} ${styles.formControlDisabled}`} 
                      placeholder="E-mail" 
                      value={form.email} 
                      readOnly 
                      disabled
                      title="Email address cannot be changed"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <input 
                      type="password" 
                      name="password" 
                      className={styles.formControl} 
                      placeholder="New Password" 
                      value={form.password} 
                      onChange={handleChange} 
                      required 
                      minLength="8"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <input 
                      type="password" 
                      name="password_confirmation" 
                      className={styles.formControl} 
                      placeholder="Confirm New Password" 
                      value={form.password_confirmation} 
                      onChange={handleChange} 
                      required 
                      minLength="8"
                    />
                  </div>

                  {error && (
                    <div className={styles.textDanger}>{error}</div>
                  )}

                  {success && (
                    <div className={styles.textSuccess}>{success}</div>
                  )}

                  <button 
                    type="submit" 
                    className={styles.btnLogin} 
                    disabled={isLoading}
                  >
                    {isLoading ? 'Resetting...' : 'Reset password'}
                  </button>
                </form>

                <a className={styles.forgotPassword} href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>
                  Back to login
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 